import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import DOMPurify from 'dompurify';
import debounce from 'lodash/debounce.js';
import throttle from 'lodash/throttle.js';
import labelsMixin from '../../mixins/labels.js';
import { keyCodes } from '../../utils/utils.js';
import get from 'lodash/get.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
const DEFAULT_DURATION = 300;
function _empty() {}
function _invokeIgnored(body) {
  var result = body();
  if (result && result.then) {
    return result.then(_empty);
  }
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var script = {
  name: 'PendoInput',
  mixins: [labelsMixin],
  inject: {
    $form: {
      default: ''
    },
    $formItem: {
      default: ''
    }
  },
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: null
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    },
    sanitize: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'medium',
      validator: size => ['mini', 'small', 'medium'].includes(size)
    },
    type: {
      type: String,
      default: 'text'
    },
    width: {
      type: String,
      default: '100%'
    },
    debounce: {
      type: [Boolean, Number],
      default: false
    },
    throttle: {
      type: [Boolean, Number],
      default: false
    },
    resize: {
      type: [String, Boolean],
      default: false,
      validator: resize => ['vertical', 'horizontal', 'auto'].includes(resize) || typeof resize === 'boolean'
    },
    autosize: {
      type: Boolean,
      default: true
    },
    invalid: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      focused: false,
      lazyValue: this.value
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        this.lazyValue = val;
        this.$emit('input', this.lazyValue);
      }
    },
    actualWidth() {
      const {
        width
      } = this;
      switch (width) {
        case 'medium':
          return '232px';
        case 'small':
          return '160px';
        case 'mini':
          return '80px';
      }
      return width;
    },
    delayDuration() {
      if (this.debounce) {
        return typeof this.debounce !== typeof true ? Number(this.debounce) : DEFAULT_DURATION;
      }
      return typeof this.throttle !== typeof true ? Number(this.throttle) : DEFAULT_DURATION;
    },
    isDisabled() {
      const isFormDisabled = get(this, '$form.disabled', false);
      return this.disabled || isFormDisabled;
    },
    ariaLabelledBy() {
      if (this.$formItem) {
        return this.$formItem.labelId;
      }
      if (this.hasTopLabel) {
        return `pendo-input__label--top-${this._uid}`;
      }
      if (this.hasBottomLabel) {
        return `pendo-input__label--bottom-${this._uid}`;
      }
      return undefined;
    },
    mergedAttributes() {
      return _objectSpread2({
        'id': get(this, '$formItem.labelFor'),
        'aria-describedby': get(this, '$formItem.ariaDescribedBy'),
        'aria-invalid': Boolean(get(this, '$formItem.ariaDescribedBy')),
        'aria-labelledby': this.ariaLabelledBy,
        'required': get(this, '$formItem.isRequired', false)
      }, this.$attrs);
    }
  },
  watch: {
    value(val) {
      this.lazyValue = val;
      if (this.$formItem) {
        this.$formItem.onFieldChange(val);
      }
    }
  },
  created() {
    if (this.debounce || this.throttle) {
      const strategies = {
        throttle,
        debounce
      };
      const strategy = this.debounce ? 'debounce' : 'throttle';
      this.onInput = strategies[strategy](this.onInput, this.delayDuration);
    }
  },
  beforeDestroy() {
    if (this.type === 'textarea' && !this.resize && this.autosize) {
      this.$refs.textarea.removeEventListener('input', this.resizeTextarea);
    }
  },
  mounted: _async(function () {
    const _this = this;
    if (_this.autofocus) {
      _this.focus();
    }
    return _invokeIgnored(function () {
      if (_this.type === 'textarea' && !_this.resize && _this.autosize) {
        return _await(_this.$nextTick(), function () {
          _this.$refs.textarea.addEventListener('input', _this.resizeTextarea);
          _this.$refs.textarea.style.height = `${_this.$refs.textarea.scrollHeight + 2}px`;
        });
      }
    });
  }),
  methods: {
    focus() {
      this.onFocus();
    },
    blur() {
      requestAnimationFrame(() => {
        const ref = this.type === 'textarea' ? 'textarea' : 'input';
        if (this.$refs[ref]) {
          this.$refs[ref].blur();
        }
      });
    },
    onChange(event) {
      if (this.onInput.flush) {
        this.onInput.flush(event.target.value);
      }
      this.updateValue(event.target.value);
      this.$emit('change', this.internalValue);
      this.$emit('onChange', this.internalValue);
    },
    onKeydown(event) {
      if (event.keyCode === keyCodes.enter) {
        this.onChange(event);
      }
      if (event.keyCode === keyCodes.esc) {
        this.blur();
      }
      this.$emit('keydown', event);
    },
    onFocus(event) {
      const ref = this.type === 'textarea' ? 'textarea' : 'input';
      if (document.activeElement !== this.$refs[ref]) {
        return this.$refs[ref].focus();
      }
      if (!this.focused) {
        this.focused = true;
        if (event) {
          this.$emit('focus', event);
        }
      }
    },
    onBlur: _async(function (event) {
      const _this2 = this;
      _this2.focused = false;
      return _invokeIgnored(function () {
        if (event) {
          return _await(_this2.$nextTick(), function () {
            _this2.$emit('blur', event);
            if (_this2.$formItem) {
              _this2.$formItem.onFieldBlur(_this2.internalValue);
            }
          });
        }
      });
    }),
    onInput(event) {
      this.updateValue(event.target.value);
    },
    updateValue(value) {
      if (this.sanitize) {
        value = DOMPurify.sanitize(value);
      }
      this.internalValue = value;
    },
    resizeTextarea() {
      let event = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      this.$refs.textarea.style.height = 'auto';
      if (event) {
        this.$refs.textarea.style.height = `${event.target.scrollHeight + 2}px`;
      }
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-input.vue2.js.map
